import shopDetailsSymbols from "../symbols/shopDetails";
import { HttpRequestGet, HttpRequestPut, HttpRequestPost } from "../../service";
import { AxiosError } from "axios";

// Shop user types
export interface ShopUserEmailDetails {
  Email: string;
  NotificationsEnabled: boolean;
}

export interface ShopUserPhoneDetails {
  PhoneNumber: string;
  NotificationsEnabled: boolean;
}

export interface ShopUserDetails {
  Id: string;
  FirstName?: string;
  LastName?: string;
  Email?: ShopUserEmailDetails;
  PhoneNumber?: ShopUserPhoneDetails;
}

// Response models
export interface ShopDetailsResponseModel {
  Id: string;
  PhoneNumber: string | null;
  InfoEmail: string | null;
  Name: string;
  ShopImageUrl: string | null;
  Address: {
    Line1: string | null;
    Line2: string | null;
    Line3: string | null;
    PostalCode: string | null;
    County: string | null;
    City: string | null;
    Lat: number | null;
    Long: number | null;
  };
  TkdEnabled: boolean;
  MarketplaceEnabled: boolean;
  OrkestroDisplayName: string | null;
  BusinessName: string | null;
  Users: ShopUserDetails[];
  WorkingHours: {
    Id: string;
    Day: number;
    StartTime: string;
    EndTime: string;
  }[];
}

// Request models
export interface EditShopDetailsRequestModel {
  PhoneNumber?: string;
  InfoEmail?: string;
  Name?: string;
  ShopImageBase64?: string;
  Address?: {
    Line1?: string;
    Line2?: string;
    Line3?: string;
    PostalCode?: string;
    County?: string;
    City?: string;
    Lat?: number;
    Long?: number;
  };
  TkdEnabled?: boolean;
  MarketplaceEnabled?: boolean;
  OrkestroDisplayName?: string;
}

export interface ShopWorkingDayAndHourRequestModel {
  Id?: string;
  Day: number;
  StartHour: number;
  StartMinute: number;
  EndHour: number;
  EndMinute: number;
  ShouldBeDeleted: boolean;
}

export interface ShopWorkingDayAndHourRequest {
  WorkingHours: ShopWorkingDayAndHourRequestModel[];
}

// Actions
const shopDetailsSymbolsAction = () => ({
  type: shopDetailsSymbols.SHOP_DETAILS,
});
const shopDetailsSymbolsSuccessAction = (data: any) => ({
  type: shopDetailsSymbols.SHOP_DETAILS_SUCCESS,
  payload: data,
});
const shopDetailsSymbolsFailureAction = (data: any) => ({
  type: shopDetailsSymbols.SHOP_DETAILS_FAILURE,
  payload: data,
});
const clearshopDetailsSymbolsAction = () => ({
  type: shopDetailsSymbols.CLEAR_SHOP_DETAILS,
});

// New callback-based function for admin shop details
export function getShopDetails(
  merchantId: string,
  shopId: string,
  success: (response: ShopDetailsResponseModel) => void,
  failure: (error: AxiosError<any>) => void
): void {
  HttpRequestGet(
    `/admin/merchant/${merchantId}/shop/${shopId}`,
    () => undefined,
    (response: { Result: ShopDetailsResponseModel } | ShopDetailsResponseModel) => {
      // Handle both wrapped and unwrapped responses
      const data = (response as any).Result || response;
      success(data);
    },
    (error: any) => failure(error),
    true
  );
}

// Legacy Redux action for backward compatibility with existing ShopDetails.tsx
export function getShopDetailsLegacy(id: string) {
  return (dispatch: any) => {
    dispatch(shopDetailsSymbolsAction());
    HttpRequestGet(
      "/merchant/shop/" + id,
      dispatch,
      shopDetailsSymbolsSuccessAction,
      shopDetailsSymbolsFailureAction,
      true
    );
  };
}

export function editShopDetails(
  merchantId: string,
  shopId: string,
  data: EditShopDetailsRequestModel,
  success: (response: any) => void,
  failure: (error: AxiosError<any>) => void
): void {
  HttpRequestPut(
    `/admin/merchant/${merchantId}/shop/${shopId}`,
    () => undefined,
    (response: any) => success(response),
    (error: any) => failure(error),
    true,
    data
  );
}

export function setShopWorkingHours(
  merchantId: string,
  shopId: string,
  data: ShopWorkingDayAndHourRequest,
  success: (response: any) => void,
  failure: (error: AxiosError<any>) => void
): void {
  HttpRequestPut(
    `/admin/merchant/${merchantId}/shop/${shopId}/working-hours`,
    () => undefined,
    (response: any) => success(response),
    (error: any) => failure(error),
    true,
    data
  );
}

export function clearshopDetails() {
  return (dispatch: any) => {
    dispatch(clearshopDetailsSymbolsAction());
  };
}

// Shop user management interfaces
export interface EditShopUserData {
  FirstName?: string;
  LastName?: string;
  Email?: string;
  PhoneNumber?: string;
  EmailNotificationsEnabled?: boolean;
  PhoneNotificationsEnabled?: boolean;
}

export interface InviteShopUserData {
  FirstName: string;
  LastName: string;
  Email: string;
  PhoneNumber?: string;
  EmailNotificationsEnabled?: boolean;
  PhoneNotificationsEnabled?: boolean;
}

export function editShopUser(
  merchantId: string,
  shopId: string,
  userId: string,
  data: EditShopUserData,
  success: (response: any) => void,
  failure: (error: AxiosError<any>) => void
): void {
  HttpRequestPut(
    `/admin/merchant/${merchantId}/shop/${shopId}/user/${userId}/edit`,
    () => undefined,
    (response: any) => success(response),
    (error: any) => failure(error),
    true,
    data
  );
}

export interface InviteShopUserResponseModel {
  Password: string;
}

export function inviteShopUser(
  merchantId: string,
  shopId: string,
  data: InviteShopUserData,
  success: (response: InviteShopUserResponseModel) => void,
  failure: (error: AxiosError<any>) => void
): void {
  HttpRequestPost(
    `/admin/merchant/${merchantId}/shop/${shopId}/user/invite`,
    () => undefined,
    (response: { Result: InviteShopUserResponseModel }) => {
      const data = (response as any).Result || response;
      success(data);
    },
    (error: any) => failure(error),
    true,
    data
  );
}
